import type { ReactNode } from 'react';
import { cn } from './cn.ts';

/**
 * A plain semantic table.
 *
 * React Aria's `Table` is the right answer for a grid a person navigates with
 * arrow keys or selects rows in; every table in this shell is a read-out with
 * links and buttons inside its cells, and a grid role would make those cells
 * something to arrow into rather than tab through. Native table semantics are
 * both correct and what a screen reader announces best here.
 */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full border-collapse text-sm', className)}>{children}</table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="bg-secondary">{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-secondary">{children}</tbody>;
}

export function TR({ children, className }: { children: ReactNode; className?: string }) {
  return <tr className={cn(className)}>{children}</tr>;
}

export function TH({
  children,
  className,
  scope = 'col',
}: {
  children?: ReactNode;
  className?: string;
  scope?: 'col' | 'row';
}) {
  return (
    <th
      scope={scope}
      className={cn(
        'border-b border-secondary px-4 py-2.5 text-left text-xs font-semibold text-tertiary',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function TD({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 align-top text-secondary', className)}>{children}</td>;
}

export function TableEmpty({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-tertiary">
        {children}
      </td>
    </tr>
  );
}
